import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatFecha } from '../utils/format';
import AutocompleteProducto from '../components/AutocompleteProducto';
import PreciosPorProveedor from '../components/PreciosPorProveedor';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';

// Comparador de precios de compra entre proveedores (#100): se elige un
// insumo (solo los que tienen compras con proveedor) y se ve la misma tabla
// "Precios por proveedor" de la ficha del producto, con el historial abierto.
export default function ComparadorPrecios() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [productoId, setProductoId] = useState(null);
  const [tasa, setTasa] = useVistaMoneda();

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    setError('');
    setProductoId(null);
    api.get('/producto-costo-historial/productos-comprados')
      .then(({ data }) => setProductos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudieron cargar los productos'))
      .finally(() => setCargando(false));
  }, [empresaActiva]);

  const producto = productos.find((p) => String(p.id) === String(productoId));

  // Mismos datos que la tabla de pantalla, pedidos de nuevo al exportar.
  async function contenidoExcel() {
    const { data } = await api.get('/producto-costo-historial/por-proveedor', { params: { producto_id: producto.id, tasa } });
    const precio = (v) => (v == null ? 'sin tasa' : Number(v));
    return {
      titulo: `Comparador de precios ${producto.nombre || ''}`.trim(),
      detalle: `${producto.nombre || ''} · precios por ${producto.unidad_medida || 'unidad'} en ${tasa}, con las tasas del día de cada compra`,
      hojas: [
        {
          nombre: 'Por proveedor',
          columnas: [
            { titulo: 'Proveedor' }, { titulo: `Último precio ${tasa}`, tipo: 'precio' }, { titulo: 'Fecha', tipo: 'fecha' },
            { titulo: `Mínimo ${tasa}`, tipo: 'precio' }, { titulo: `Máximo ${tasa}`, tipo: 'precio' }, { titulo: 'Compras', tipo: 'entero' }
          ],
          filas: data.proveedores.map((p) => [p.proveedor_nombre, precio(p.ultimo), p.fecha_ultimo, precio(p.minimo), precio(p.maximo), Number(p.compras)])
        },
        {
          nombre: 'Historial',
          columnas: [
            { titulo: 'Fecha', tipo: 'fecha' }, { titulo: 'Proveedor' }, { titulo: 'Factura' }, { titulo: 'Moneda' },
            { titulo: 'Precio original', tipo: 'precio' }, { titulo: `Precio ${tasa}`, tipo: 'precio' }
          ],
          filas: data.historial.map((h) => [h.fecha, h.proveedor_nombre, h.factura_numero ? `#${h.factura_numero}` : 'manual', h.moneda === 'BS' ? 'Bs' : 'USD', Number(h.costo_unitario), precio(h.costo_vista)])
        }
      ]
    };
  }

  return (
    <div className="space-y-6">
      <EncabezadoImpresion
        titulo="Comparador de precios"
        detalle={producto ? `${producto.nombre || ''} · precios por ${producto.unidad_medida} en ${tasa}` : `Precios en ${tasa}`}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Comparador de precios
            {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
            <BotonAyuda />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            A qué precio te vendió cada proveedor un mismo insumo, convertido con las tasas del día de cada compra.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SelectorMoneda vista={tasa} onChange={setTasa} />
          <Exportar excel={contenidoExcel} disabled={!producto} />
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {cargando ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : productos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">
          Todavía no hay compras con proveedor registradas en esta empresa.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3 print:hidden">
            <label className="text-sm text-slate-600">Insumo</label>
            <div className="w-full sm:w-96">
              <AutocompleteProducto
                productos={productos}
                value={productoId}
                onChange={setProductoId}
                placeholder="Buscar un producto comprado…"
              />
            </div>
            {producto && (
              <span className="text-xs text-slate-400">
                {producto.proveedores} {Number(producto.proveedores) === 1 ? 'proveedor' : 'proveedores'}
                {' · '}{producto.compras} {Number(producto.compras) === 1 ? 'compra' : 'compras'}
                {' · '}última {formatFecha(producto.ultima_compra)}
                {' · '}precios por {producto.unidad_medida}
              </span>
            )}
          </div>

          {producto ? (
            <PreciosPorProveedor productoId={producto.id} tasa={tasa} unidad={producto.unidad_medida} pantalla />
          ) : (
            <p className="text-sm text-slate-400">Elige un insumo para comparar sus proveedores.</p>
          )}
        </>
      )}
    </div>
  );
}
